import { FC } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipProps,
} from "recharts";
import { DefinitionTooltip } from "src/components/common";
import { CHART_PRIMARY_COLOR } from "src/components/charts/ChartPalette";
import { mirrorChartMargin, rtlChartProps } from "src/components/charts/ChartRtl";
import type { FollowersTrendGranularity } from "../../../hooks/UseOrganicMediaData";
import { formatNumber, isArabicLanguage, toArabicIndicDigitsInString } from "src/utils/numberFormat";
import "../../../styles/FacebookFollowersReport/FacebookNewFollowers.scss";

export type FacebookNewFollowersPoint = {
  date: string;
  value: number;
  valueFormattedValue?: string;
};

type FacebookNewFollowersProps = {
  trendChartData: FacebookNewFollowersPoint[];
  trendGranularity: FollowersTrendGranularity;
  onTrendGranularityChange: (_value: FollowersTrendGranularity) => void;
};

const formatTrendAxisDate = (value: string | number) => {
  const raw = String(value ?? "");
  const isoMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }

  const monthMatch = raw.match(/^(\d{4})-(\d{2})$/);
  if (monthMatch) {
    return `${monthMatch[2]}-${monthMatch[1]}`;
  }

  return raw;
};

function FollowersTrendTooltip({ active, label, payload }: TooltipProps<number, string>) {
  const { t, i18n } = useTranslation();
  if (!active || !payload?.length) {
    return null;
  }

  const point = payload[0]?.payload as { valueFormattedValue?: string; dateFormattedValue?: string } | undefined;
  const isRTL = i18n.language?.toLowerCase().startsWith("ar");

  return (
    <div className="organic-tooltip organic-tooltip--kpi">
      <div className="organic-tooltip__bar" />
      <div className="organic-tooltip__content">
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">
            {t("organicMedia.tooltips.date")}
          </span>
          <strong className="organic-tooltip__meta-value">
            {point?.dateFormattedValue ??
              (isRTL
                ? toArabicIndicDigitsInString(formatTrendAxisDate(String(label ?? "")))
                : formatTrendAxisDate(String(label ?? "")))}
          </strong>
        </div>
        <div className="organic-tooltip__meta-line">
          <span className="organic-tooltip__meta-label">{t("organicMedia.tooltips.newFollowers")}</span>
          <strong className="organic-tooltip__meta-value">
            {point?.valueFormattedValue ?? formatNumber(Number(payload[0]?.value ?? 0), {}, i18n.language)}
          </strong>
        </div>
      </div>
    </div>
  );
}

const FacebookNewFollowers: FC<FacebookNewFollowersProps> = ({
  trendChartData,
  trendGranularity,
  onTrendGranularityChange,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = isArabicLanguage(i18n.language);
  const chartRtl = rtlChartProps(isRTL);

  return (
    <div className="organic-followers-trend organic-followers-trend--modern organic-content-panel">
      <div className="organic-content-panel__header">
        <div className="organic-content-panel__title">
          <h3>{t("organicMedia.followers.newFollowersTitle")}</h3>
          <DefinitionTooltip
            label={t("organicMedia.followers.newFollowersTitle")}
            info={{
              description: t("organicMedia.followers.newFollowersDescription"),
              formula: t("organicMedia.followers.newFollowersFormula"),
            }}
            formulaTitle={t("organicMedia.common.formula")}
          />
        </div>
        <div className="organic-followers-trend__toggle" role="group" aria-label={t("organicMedia.followers.trendGranularityAria")}>
          {(["daily", "monthly"] as const).map(option => (
            <button
              key={option}
              type="button"
              className={
                trendGranularity === option
                  ? "organic-followers-trend__toggle-button organic-followers-trend__toggle-button--active"
                  : "organic-followers-trend__toggle-button"
              }
              onClick={() => onTrendGranularityChange(option)}
            >
              {t(`organicMedia.contentReport.${option}`)}
            </button>
          ))}
        </div>
      </div>
      {trendChartData.length > 0 ? (
        <ResponsiveContainer width="100%" height={260}>
          <LineChart
            data={trendChartData}
            margin={mirrorChartMargin({ top: 8, right: 14, left: -8, bottom: 0 }, isRTL)}
          >
            <CartesianGrid stroke="#e8eef6" strokeDasharray="4 4" vertical={false} />
            <XAxis
              dataKey="date"
              reversed={chartRtl.axisReversed}
              tick={{ fill: "#8aa0bd", fontSize: 12 }}
              tickFormatter={value =>
                i18n.language?.toLowerCase().startsWith("ar")
                  ? toArabicIndicDigitsInString(formatTrendAxisDate(value))
                  : formatTrendAxisDate(value)
              }
              axisLine={{ stroke: "#d7dde7" }}
              tickLine={false}
              interval="preserveStartEnd"
              height={30}
            />
            <YAxis
              orientation={chartRtl.yAxisOrientation}
              tick={{ fill: "#8aa0bd", fontSize: 12 }}
              tickFormatter={value => formatNumber(value, {}, i18n.language)}
              axisLine={{ stroke: "#d7dde7" }}
              tickLine={false}
              width={44}
            />
            <Tooltip
              content={<FollowersTrendTooltip />}
              allowEscapeViewBox={{ x: false, y: false }}
              wrapperStyle={{ pointerEvents: "none" }}
            />
            <Line
              type="monotone"
              dataKey="value"
              name={t("organicMedia.followers.newFollowersTitle")}
              stroke={CHART_PRIMARY_COLOR}
              strokeWidth={2.5}
              dot={false}
              activeDot={{ r: 4 }}
            />
          </LineChart>
        </ResponsiveContainer>
      ) : (
        <div className="organic-followers-trend__empty">
          {t("webAnalytics.noData")}
        </div>
      )}
    </div>
  );
};

export default FacebookNewFollowers;
